<template>
  <div class="p-2">
    <base-dialog v-model="loginEdgeCaseDialog.visible" :okFn="async () => { defaultLoginCallback(loginInfo) }" :content="loginEdgeCaseDialog.content"></base-dialog>
    <base-dialog keepOpened v-model="termsAndConditionsModalVisible" :okFn="async () => { acceptTerms() }" :cancelFn="() => { denyTerms() }">
			{{ $t('login.terms-and-conditions') }}
			<br><br>
			<div class="terms-ctn">
				<TermsAndConditionsPatient v-if="userRoleIsPatient"/>
				<TermsAndConditionsProfessional v-else/>
			</div>
			<base-checkbox v-model="termsAndConditionsAccepted" :state="termsAndConditionsAccepted">
				{{ $t('login.terms-and-conditions-accept') }}
			</base-checkbox>
		</base-dialog>
		<base-dialog keepOpened v-model="privacyPolicyModalVisible" :okFn="async () => { acceptPrivacyPolicy() }" :cancelFn="() => { denyPrivacyPolicy() }">
			{{ $t('login.privacy-policy') }}
			<br><br>
			<div class="terms-ctn">
				<PrivacyPolicy v-if="userRoleIsPatient"/>
				<PrivacyPolicyProfessional v-else/>
			</div>
			<base-checkbox v-model="privacyPolicyAccepted" :state="privacyPolicyAccepted">
				{{ $t('login.privacy-policy-accept') }}
			</base-checkbox>
		</base-dialog>
		<h3 v-if="titleVisible">{{ title }}</h3>
		<!-- <loading-anim :active="form.state.waiting"></loading-anim> -->
    <b-form @submit.stop.prevent="login">
      <b-alert show variant="danger" v-if="form.state.submitted && form.state.unauthorized">{{ submitError }}</b-alert>
      <b-alert show variant="danger" v-if="checkCredentials">{{ !mfaVisible ? $t('invalid-credentials') : $t('invalid-2facode')}}</b-alert>
      <b-alert show variant="danger" v-if="form.state.submitted && form.state.errors"><span v-html="accountError || $t('login.server-error')"></span></b-alert>
      <b-form-group v-if="!mfaVisible">
        <base-input v-model="form.email" :placeholder="$t('login.email')" :state="emailValidation" :errorMsg="$t('login.errors.email')" autocomplete="disabled"></base-input>
        <base-input v-model="form.password" :placeholder="$t('login.password')" :type="showPassword ? 'text' : 'password'" :state="passwordValidation" :errorMsg="$t('login.errors.password')" autocomplete="disabled">
          <template slot="append">
            <span @click="showPassword = !showPassword">
              <i><b-icon-eye v-if="!showPassword"></b-icon-eye><b-icon-eye-slash v-if="showPassword"></b-icon-eye-slash></i>
            </span>
          </template>
        </base-input>
      </b-form-group>
      <b-form-group v-else>
        <base-input v-model="form.mfaCode" :placeholder="$t('login.twoFACode-ph')" :label="$t('login.twoFACode')"></base-input>
      </b-form-group>
      <b-button type="submit" variant="primary" style="width: 100%" :disabled="$store.state.loading">
        {{ $t('login.sign-in') }}
      </b-button>
      <b-button @click="$helpers.gotoACAUrl"  variant="outline-primary" class="mt-4" style="width: 100%" v-if="inOpenlegal()">
        {{ $t('login.aca-sign-in') }}
      </b-button>
      <hr>
      <router-link to="/cuenta/peticion-actualizar-contrasena" tag="a">{{ $t('login.forget-password') }}</router-link>
    </b-form>
  </div>
</template>

<script>
import { BForm, BFormGroup, BButton, BFormInvalidFeedback, BFormInput, BAlert, BIconEyeSlash, BIconEye } from 'bootstrap-vue'
import accountRepository from '@/repositories/AccountRepository'
import LoadingAnim from '@/components/utils/LoadingAnim.vue'
import { PRIVACY_POLICY_ACTION, TERMS_AND_CONDITIONS_ACTION, CHECK_CREDENTIALS_ACTION, PASSWORD_MAX_ATTEMPTS_ACTION, PASSWORD_UPDATE_ACTION, REENABLE_ACCOUNT_ACTION, TWO_FACTOR_ACTION } from '@/constants/loginAdditionalActions'
import domains from '_static/domains.json'
import { LOGIN_EDGE_CASES } from '@/constants/loginEdgeCases'
import appHelpers from '@/helpers/app.helpers'
import { PATIENT_ROLE } from '../../constants/roles'
import BaseDialog from '@/components/base/BaseDialog.vue'
import { inOpensalud360, inOpenlegal } from '@/constants/platformNames'
import filters from '@/plugins/filters'
import WebappRepository from '@/repositories/WebappRepository'
import TermsAndConditionsPatient from '@/components/views/TermsAndConditionsPatient.vue'
import TermsAndConditionsProfessional from '@/components/views/TermsAndConditionsProfessional.vue'
import PrivacyPolicy from '@/components/views/PrivacyPolicy.vue'
import PrivacyPolicyProfessional from '@/components/views/PrivacyPolicyProfessional.vue'
import accountMixin from '@/mixins/account.mixin'
import { HEALTHY_HABITS_SUBDOMAIN } from '@/constants/subdomains'

export default {
	mixins: [accountMixin],
  components: {
    BForm,
    BFormGroup,
    BButton,
    BFormInvalidFeedback,
    BFormInput,
    BAlert,
    BIconEyeSlash,
    BIconEye,
    LoadingAnim,
    BaseDialog,
		TermsAndConditionsPatient,
		TermsAndConditionsProfessional,
		PrivacyPolicy,
		PrivacyPolicyProfessional
},
  props: {
    loginCallback: {
      type: Function,
      required: false,
      default: null
    },
		title: {
			type: String,
			required: false,
			default: null
		}
  },
  data () {
    return {
			inOpenlegal,
      showPassword: false,
      form: {
        email: null,
        password: null,
        csrfToken: null,
        state: {
          waiting: false,
          unauthorized: false,
          errors: false,
          submitted: false
        },
        mfaCode: null
      },
      errors: {
        email: null,
        password: null
      },
      submitError: null,
      visibility: 'password',
      mfaVisible: false,
      checkCredentials: false,
      accountError: null,
      domains,
      loginInfo: null,
      loginEdgeCaseDialog: {
        visible: false,
        content: null
      },
			termsAndConditionsModalVisible: false,
			termsAndConditionsAccepted: null,
			privacyPolicyModalVisible: false,
			privacyPolicyAccepted: null
    }
  },
  methods: {
    async login () {
      this.resetForm()
      this.form.state.submitted = true
      this.form.state.waiting = true
      if (this.formValid !== false) {
				accountRepository.login(this.form.email.trim(), this.form.password.trim(), this.form.mfaCode ? this.form.mfaCode.trim() : null).then(async (response) => {
          this.$store.commit('setLoading', true)
          const responseData = response.data || (response.response ? response.response.data : null)
          const hasExceptionType = (response.response && response.response.status !== 200 && response.response.data.ExceptionType)
          const hasErrorMessage = (response.data && response.data.error && response.data.error.errorMessage)
          if ((hasExceptionType || hasErrorMessage) && (!responseData || !responseData.requiredAction)) {
            const msgKey = hasExceptionType ? response.response.data.ExceptionType : response.data.error.errorMessage
            const transKey = `login.errors.${msgKey}`
            this.showError(this.$te(transKey) ? this.$t(transKey) : null)
          } else if (!response.data && !responseData.requiredAction && response.message) {
            this.showError(response.message)
          } else {
            switch (responseData.requiredAction) {
              case (CHECK_CREDENTIALS_ACTION):
                this.checkCredentials = true
                break
              case (PASSWORD_MAX_ATTEMPTS_ACTION):
								this.checkCredentials = false
                this.showError(this.$t('login.errors.passwordMaxAttempts'))
                break
              case (PASSWORD_UPDATE_ACTION):
                await this.$router.push({ path: '/cuenta/peticion-actualizar-contrasena', query: { expired: true } })
                break
              case (REENABLE_ACCOUNT_ACTION):
                await this.$router.push({ path: '/cuenta/reactivar', query: { email: this.form.email.trim() } })
                break
              case (TWO_FACTOR_ACTION):
                this.checkCredentials = false
                this.mfaVisible = true
                break
							case (TERMS_AND_CONDITIONS_ACTION):
								this.loginInfo = responseData
								this.termsAndConditionsModalVisible = true
								break
							case (PRIVACY_POLICY_ACTION):
								this.loginInfo = responseData
								this.privacyPolicyModalVisible = true
								break
              default:
                const token = responseData.apiToken
                this.loginInfo = responseData
								this.$store.dispatch('session/set', responseData)
                if (responseData.edgeCase && inOpensalud360()) {
                  const edgeCaseObj = LOGIN_EDGE_CASES(appHelpers.getV2AppUrl(), responseData.travelCompanyName, filters.beautifyDate(responseData.user.companyCode.startDate), filters.beautifyDate(responseData.user.companyCode.endDate), responseData.apiToken)[responseData.edgeCase]
                  if (edgeCaseObj && edgeCaseObj.view) {
                    this.$store.commit('setErrorPageMessage', this.$t(edgeCaseObj.message))
                    await this.$router.push(edgeCaseObj.view)
                  } else {
                    this.showLoginEdgeCaseDialog(this.$t(edgeCaseObj.message))
                  }
                  break
								} else if (responseData.requiredFields && responseData.requiredFields.length > 0) {
									this.processRequiredFields(responseData.user, responseData.requiredFields, token)
                } else if (this.loginCallback) {
                  this.loginCallback()
                  break
                } else if (this.$store.state.session.loginCallback) {
                  this.$store.state.session.loginCallback()
                  break
                } else {
                  await this.defaultLoginCallback(responseData)
                }
                break
            }
          }
          this.form.state.waiting = false
          this.$store.commit('setLoading', false)
          this.$store.dispatch('signalr/init')
        })
      }
    },
    resetForm: function () {
      this.form.state.submitted = false
      this.form.state.waiting = false
      this.form.state.unauthorized = false
      this.form.state.errors = false
    },
    showError (error) {
      this.form.state.errors = true
      this.accountError = error
    },
    showLoginEdgeCaseDialog (content) {
      this.loginEdgeCaseDialog = {
        visible: true,
        content
      }
    },
		acceptTerms () {
			if (this.termsAndConditionsAccepted === true) {
				WebappRepository.updateReadTermsDate(this.loginInfo.apiToken).then(async (response) => {
					if (response.data) {
						this.termsAndConditionsModalVisible = false
						await this.login()
					}
				})
			} else {
				this.termsAndConditionsAccepted = false
			}
		},
		denyTerms () {
			this.termsAndConditionsModalVisible = false
			window.location.reload()
		},
		acceptPrivacyPolicy () {
			if (this.privacyPolicyAccepted === true) {
				WebappRepository.updateReadPrivacyPolicyDate(this.loginInfo.apiToken).then(async (response) => {
					if (response.data) {
						this.privacyPolicyModalVisible = false
						await this.login()
					}
				})
			} else {
				this.privacyPolicyAccepted = false
			}
		},
		denyPrivacyPolicy () {
			this.privacyPolicyModalVisible = false
			window.location.reload()
		}
  },
  computed: {
    formValid: function () {
      return this.emailValidation && this.passwordValidation
    },
    emailValidation: function () {
      return this.$validators.validate(this.form.email, ['required', 'email'], this.form.state.submitted)
    },
    passwordValidation: function () {
      return this.$validators.validate(this.form.password, ['required'], this.form.state.submitted)
    },
		userRoleIsPatient () {
			return this.loginInfo && this.loginInfo.user && this.loginInfo.user.roles && this.loginInfo.user.roles.includes(PATIENT_ROLE)
		},
		titleVisible () {
			return this.title && this.mfaVisible === false
		}
  },
  created () {
    if (window.location.hostname.includes(HEALTHY_HABITS_SUBDOMAIN) && !this.$store.state.session.loginCallback) {
      let _this = this
      this.$store.commit('session/setLoginCallback', function () {
        _this.$router.push('/').catch(() => {})
      })
    }
	}
}
</script>

<style lang="scss" scoped>
.eyeIconPass{
  cursor: pointer;
  position: absolute;
  margin: -9.5% 0px 0px 33%;
  }

/*   @include desktop {
    .alert {
      max-width: 300px;
    }
  } */


.terms-ctn {
	max-height: 300px;
	overflow-y: auto;
	padding: 10px;
	border: 1px solid #ccc;
	border-radius: 5px;
	margin-bottom: 10px;
	border-radius: $border-radius;
}
</style>



// WEBPACK FOOTER //
// src/components/forms/LoginForm.vue